import { useState, useEffect } from "react";
import axios from "axios";
import { 
  ChartLine, 
  VideoCamera, 
  Brain,
  Question,
  ListChecks,
  Microphone,
  ShieldWarning,
  ArrowsClockwise,
  Lightbulb,
  SpinnerGap,
  Warning,
  CheckCircle,
  XCircle,
  Clock,
  TrendUp,
  ChatCircle,
  Target
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);

export default function AnalyticsPage() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashboardRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/api/admin/dashboard`, { withCredentials: true }),
        axios.get(`${API}/api/admin/analytics`, { withCredentials: true })
      ]);
      
      setDashboard(dashboardRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error("Failed to load analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  // Vincent #169 (Eric 2026-05-26 round 4) + Vincent #506 (2026-07-21) —
  // Toutes les étiquettes traduites en français. #506 retire "Réfutations"
  // qui est un doublon d'"Objections" (le pipeline n'extrait plus les
  // rebuttals séparément, elles sont fusionnées avec les objections).
  const knowledgeTypes = [
    { key: "qa", label: L(lang, "Q&R", "Q&A"), icon: Question, color: "bg-blue-100 text-blue-700" },
    { key: "procedure", label: L(lang, "Procédures", "Procédures"), icon: ListChecks, color: "bg-blue-100 text-blue-700" },
    { key: "script", label: L(lang, "Scripts de vente", "Scripts"), icon: Microphone, color: "bg-yellow-100 text-yellow-700" },
    { key: "objection", label: L(lang, "Objections", "Objections"), icon: ShieldWarning, color: "bg-red-100 text-red-700" },
    { key: "concept", label: L(lang, "Concepts", "Concepts"), icon: Lightbulb, color: "bg-indigo-100 text-indigo-700" },
  ];

  const orgName = user?.org_name || L(lang, "votre organisation", "your organization");

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">
          {L(lang, `Analytique du LMS — ${orgName}`, `LMS Analytics — ${orgName}`)}
        </h1>
        <p className="text-zinc-500 mt-1">
          {L(lang,
            `Suivi de la croissance et de la santé du pipeline de connaissances pour ${orgName}. Données strictement isolées (Loi 25).`,
            `Monitor knowledge base growth and pipeline health for ${orgName}. Strictly isolated data (Law 25).`
          )}
        </p>
      </div>

      {/* Overview stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={VideoCamera}
          label={L(lang, "Vidéos totales", "Total Videos")}
          value={dashboard?.videos?.total || 0}
          sublabel={`${dashboard?.videos?.processing || 0} ${L(lang, "en traitement", "processing")}`}
        />
        <StatCard
          icon={Brain}
          label={L(lang, "Unités de connaissance", "Knowledge Units")}
          value={dashboard?.knowledge?.total || 0}
          sublabel={`${dashboard?.knowledge?.approved || 0} ${L(lang, "approuvées", "approved")}`}
        />
        <StatCard
          icon={ChatCircle}
          label={L(lang, "Requêtes (7 derniers jours)", "Queries (7 days)")}
          value={analytics?.queries_last_7_days || 0}
          sublabel={L(lang, "Questions posées", "Questions asked")}
        />
        <StatCard
          icon={Target}
          label={L(lang, "Succès du pipeline", "Pipeline Success")}
          value={`${analytics?.pipeline_success_rate || 0}%`}
          sublabel={`${L(lang, "Confiance moyenne", "Avg confidence")}: ${analytics?.avg_confidence || 0}%`}
        />
      </div>

      {/* Knowledge Growth Chart */}
      {analytics?.knowledge_growth?.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
            <TrendUp size={20} className="text-blue-600" />
            <h2 className="font-medium font-['Outfit']">{L(lang, "Croissance des connaissances (30 derniers jours)", "Knowledge Growth (Last 30 Days)")}</h2>
          </div>
          <div className="p-6">
            <div className="flex items-end gap-1 h-32">
              {analytics.knowledge_growth.map((day, i) => {
                const maxCount = Math.max(...analytics.knowledge_growth.map(d => d.count));
                const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                return (
                  <div
                    key={i}
                    className="flex-1 bg-[#002FA7] rounded-t-sm hover:bg-[#002280] transition-colors cursor-pointer group relative"
                    style={{ height: `${Math.max(height, 5)}%` }}
                    title={`${day.date}: ${day.count} units`}
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">
                      {day.date}: {day.count}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-zinc-400">
              <span>{analytics.knowledge_growth[0]?.date}</span>
              <span>{analytics.knowledge_growth[analytics.knowledge_growth.length - 1]?.date}</span>
            </div>
          </div>
        </div>
      )}

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="font-medium font-['Outfit']">{L(lang, "Statut des connaissances", "Knowledge Status")}</h2>
          </div>
          <div className="p-6">
            {analytics?.status_breakdown && Object.keys(analytics.status_breakdown).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(analytics.status_breakdown).map(([status, count]) => {
                  const total = Object.values(analytics.status_breakdown).reduce((a, b) => a + b, 0);
                  const percentage = Math.round((count / total) * 100);
                  const colors = {
                    approved: "bg-blue-500",
                    pending_review: "bg-yellow-500",
                    rejected: "bg-red-500"
                  };
                  // Vincent #169 — Traduire les statuts en français
                  // Vincent #506 (2026-07-21) — Ajout "Effacées" pour la clé
                  // `deleted` qui apparaissait en anglais.
                  const statusLabels = {
                    approved: L(lang, "Approuvées", "Approved"),
                    pending_review: L(lang, "En revue", "Pending review"),
                    rejected: L(lang, "Rejetées", "Rejected"),
                    draft: L(lang, "Brouillon", "Draft"),
                    deleted: L(lang, "Effacées", "Deleted"),
                  };
                  return (
                    <div key={status}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="capitalize">{statusLabels[status] || status.replace('_', ' ')}</span>
                        <span className="text-zinc-500">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${colors[status] || 'bg-zinc-400'} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">{L(lang, "Aucune donnée disponible", "No data available")}</div>
            )}
          </div>
        </div>

        {/* Videos by theme - moved to same grid */}
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="font-medium font-['Outfit']">{L(lang, "Vidéos par thème", "Videos by Theme")}</h2>
          </div>
          <div className="p-6">
            {analytics?.videos_by_theme && Object.keys(analytics.videos_by_theme).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(analytics.videos_by_theme).map(([theme, count]) => {
                  const total = Object.values(analytics.videos_by_theme).reduce((a, b) => a + b, 0);
                  const percentage = Math.round((count / total) * 100);
                  // Vincent #506 (2026-07-21) — Traduire les thèmes anglais.
                  const themeLabels = {
                    general: L(lang, "Général", "General"),
                    sales: L(lang, "Ventes", "Sales"),
                    compliance: L(lang, "Conformité", "Compliance"),
                    technical: L(lang, "Technique", "Technical"),
                    onboarding: L(lang, "Intégration", "Onboarding"),
                  };
                  const themeLabel = themeLabels[String(theme).toLowerCase()] || theme;
                  return (
                    <div key={theme}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="capitalize">{themeLabel}</span>
                        <span className="text-zinc-500">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#002FA7] transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">{L(lang, "Aucune vidéo pour le moment", "No videos uploaded yet")}</div>
            )}
          </div>
        </div>
      </div>

      {/* Knowledge by type */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="px-6 py-4 border-b border-zinc-200">
          <h2 className="font-medium font-['Outfit']">{L(lang, "Connaissances par type", "Knowledge by Type")}</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {knowledgeTypes.map((type) => {
              const count = analytics?.knowledge_by_type?.[type.key] || 0;
              return (
                <div key={type.key} className="text-center p-4 bg-zinc-50 rounded-sm">
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${type.color}`}>
                    <type.icon size={24} />
                  </div>
                  <div className="text-2xl font-semibold font-['Outfit']">{count}</div>
                  <div className="text-xs text-zinc-500 mt-1">{type.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pipeline status */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="px-6 py-4 border-b border-zinc-200">
          <h2 className="font-medium font-['Outfit']">{L(lang, "Vue d'ensemble du pipeline", "Pipeline Status Overview")}</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {["pending", "processing", "complete", "failed"].map((status) => {
              const count = dashboard?.pipeline_stats?.[status] || 0;
              const config = {
                pending: { icon: Clock, color: "bg-zinc-100 text-zinc-600" },
                processing: { icon: SpinnerGap, color: "bg-blue-100 text-blue-600" },
                complete: { icon: CheckCircle, color: "bg-blue-100 text-blue-600" },
                failed: { icon: XCircle, color: "bg-red-100 text-red-600" },
              }[status];
              const pipelineLabels = {
                pending: L(lang, "En attente", "Pending"),
                processing: L(lang, "En traitement", "Processing"),
                // Tâche 1389 (Eric 2026-06-30) — Coquille : la version anglaise
                // reprenait le mot français.
                complete: L(lang, "Complété", "Complete"),
                failed: L(lang, "Échoué", "Failed"),
              };
              return (
                <div key={status} className="text-center p-4 bg-zinc-50 rounded-sm">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${config.color}`}>
                    <config.icon size={20} className={status === "processing" ? "animate-spin" : ""} />
                  </div>
                  <div className="text-xl font-semibold font-['Outfit']">{count}</div>
                  <div className="text-xs text-zinc-500 mt-1">{pipelineLabels[status]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent issues */}
      {dashboard?.issues?.recent?.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="font-medium font-['Outfit']">{L(lang, "Problèmes récents du pipeline", "Recent Pipeline Issues")}</h2>
          </div>
          <div className="divide-y divide-zinc-100">
            {dashboard.issues.recent.map((issue) => (
              <div key={issue.id} className="px-6 py-4">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    issue.severity === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm">{issue.message}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-zinc-400">
                      <span>{L(lang, "Étape", "Stage")}: {issue.stage}</span>
                      <span>{new Date(issue.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chat Analytics - Recent Queries */}
      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
          <ChatCircle size={20} className="text-[#002FA7]" />
          <h2 className="font-medium font-['Outfit']">{L(lang, "Questions récentes des utilisateurs", "Recent user questions")}</h2>
          <span className="ml-auto text-xs text-zinc-400">{analytics?.queries_last_7_days || 0} {L(lang, "questions (7 jours)", "questions (7 days)")}</span>
        </div>
        <div className="divide-y divide-zinc-100">
          {analytics?.recent_queries?.length > 0 ? (
            analytics.recent_queries.map((q, i) => (
              <div key={i} className="px-6 py-3 hover:bg-zinc-50 transition-colors" data-testid={`recent-query-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Question size={16} className="text-[#002FA7]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-800 line-clamp-2">{q.question}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-zinc-400">
                      <span>{q.user_name}</span>
                      {q.team && <span className="px-1.5 py-0.5 bg-zinc-100 rounded text-zinc-500">{q.team}</span>}
                      <span>{q.created_at ? new Date(q.created_at).toLocaleString("fr-FR") : ""}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-zinc-400 text-sm">
              {L(lang, "Aucune question posée pour le moment", "No questions asked yet")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sublabel }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 bg-zinc-100 rounded-sm flex items-center justify-center">
          <Icon size={20} className="text-zinc-600" />
        </div>
      </div>
      <div className="mt-4">
        <div className="text-2xl font-semibold font-['Outfit']">{value}</div>
        <div className="text-sm text-zinc-500 mt-0.5">{label}</div>
        {sublabel && <div className="text-xs text-zinc-400 mt-1">{sublabel}</div>}
      </div>
    </div>
  );
}
